// src/pages/AuditLog.tsx
// Main Audit Log page for viewing serialized item audit history

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Shield, RefreshCw, Download, AlertCircle } from 'lucide-react';
import AuditFilters from '../components/audit/AuditFilters';
import AuditTable from '../components/audit/AuditTable';
import AuditDetailModal from '../components/audit/AuditDetailModal';
import { auditAPI } from '../services/auditAPI';
import { AuditRecord, AuditEventType, AuditQueryFilters } from '../types/audit';
import { logger } from '../utils/logger';

const auditLogger = logger.createServiceLogger('AuditLog');

const AuditLog: React.FC = () => {
  // State for audit records
  const [records, setRecords] = useState<AuditRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Filter state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [eventType, setEventType] = useState<AuditEventType | ''>('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal state
  const [selectedRecord, setSelectedRecord] = useState<AuditRecord | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return !!(startDate || endDate || eventType || searchQuery);
  }, [startDate, endDate, eventType, searchQuery]);

  // Build query filters from state
  const buildFilters = useCallback((): AuditQueryFilters => {
    const filters: AuditQueryFilters = {
      limit: 50,
    };
    
    if (startDate) {
      filters.startDate = startDate;
    }
    if (endDate) {
      filters.endDate = endDate;
    }
    if (eventType) {
      filters.eventType = eventType;
    }
    
    return filters;
  }, [startDate, endDate, eventType]);

  // Fetch audit records
  const fetchRecords = useCallback(async (append = false) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setError(null);
      }

      const filters = buildFilters();
      
      if (append && lastEvaluatedKey) {
        filters.lastEvaluatedKey = lastEvaluatedKey;
      }

      auditLogger.debug('Fetching audit records', { filters, append });

      const response = await auditAPI.getAuditRecords(filters);

      if (response.success) {
        if (append) {
          setRecords(prev => [...prev, ...response.data]);
        } else {
          setRecords(response.data);
        }
        
        setLastEvaluatedKey(response.lastEvaluatedKey);
        setHasMore(!!response.lastEvaluatedKey);
        
        auditLogger.info('Audit records fetched', { 
          count: response.data.length, 
          total: append ? records.length + response.data.length : response.data.length,
          hasMore: !!response.lastEvaluatedKey 
        });
      } else {
        throw new Error(response.error?.message || 'Failed to fetch audit records');
      }
    } catch (err: any) {
      auditLogger.error('Failed to fetch audit records', undefined, err);
      setError(err.message || 'Failed to load audit records');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [buildFilters, lastEvaluatedKey, records.length]);

  // Initial load
  useEffect(() => {
    fetchRecords();
  }, []);

  // Refetch when filters change (debounced for search)
  useEffect(() => {
    const timer = setTimeout(() => {
      setLastEvaluatedKey(null);
      fetchRecords();
    }, 300);

    return () => clearTimeout(timer);
  }, [startDate, endDate, eventType]);

  // Client-side search filtering
  const filteredRecords = useMemo(() => {
    if (!searchQuery.trim()) {
      return records;
    }

    const query = searchQuery.toLowerCase();
    return records.filter(record => 
      record.targetName.toLowerCase().includes(query) ||
      (record.targetSerial && record.targetSerial.toLowerCase().includes(query)) ||
      record.actorEmail.toLowerCase().includes(query) ||
      record.description.toLowerCase().includes(query)
    );
  }, [records, searchQuery]);

  // Handle row click
  const handleRowClick = (record: AuditRecord) => {
    setSelectedRecord(record);
    setIsModalOpen(true);
  };

  // Handle modal close
  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedRecord(null);
  };

  // Handle refresh
  const handleRefresh = () => {
    setLastEvaluatedKey(null);
    fetchRecords();
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setEventType('');
    setSearchQuery('');
  };

  // Handle load more
  const handleLoadMore = () => {
    if (hasMore && !loadingMore) {
      fetchRecords(true);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Shield className="h-7 w-7 mr-3 text-blue-600" />
            Audit Log
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Chain-of-custody audit trail for serialized equipment
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="h-5 w-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-red-800">Error loading audit records</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
            <button
              onClick={handleRefresh}
              className="text-sm font-medium text-red-800 hover:text-red-900 mt-2 underline"
            >
              Try again
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <AuditFilters
        startDate={startDate}
        endDate={endDate}
        eventType={eventType}
        searchQuery={searchQuery}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onEventTypeChange={setEventType}
        onSearchChange={setSearchQuery}
        onClearFilters={handleClearFilters}
        hasActiveFilters={hasActiveFilters}
      />

      {/* Results Count */}
      <div className="flex items-center justify-between text-sm text-gray-500">
        <span>
          Showing {filteredRecords.length} record{filteredRecords.length !== 1 ? 's' : ''}
          {searchQuery && records.length !== filteredRecords.length && (
            <span> (filtered from {records.length})</span>
          )}
        </span>
        {hasMore && (
          <span className="text-blue-600">More records available</span>
        )}
      </div>

      {/* Audit Table */}
      <AuditTable
        records={filteredRecords}
        loading={loading}
        onRowClick={handleRowClick}
      />

      {/* Load More Button */}
      {hasMore && !loading && (
        <div className="flex justify-center">
          <button
            onClick={handleLoadMore}
            disabled={loadingMore}
            className="inline-flex items-center px-6 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {loadingMore ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Loading...
              </>
            ) : (
              'Load More Records'
            )}
          </button>
        </div>
      )}

      {/* Detail Modal */}
      <AuditDetailModal
        record={selectedRecord}
        isOpen={isModalOpen}
        onClose={handleModalClose}
      />
    </div>
  );
};

export default AuditLog;
